class SchedulingUtils {
    /**
     * Normalizes campaign tokens for case-insensitive comparisons.
     * @param {string} value Campaign token.
     * @returns {string}
     */
    static normalizeCampaignToken(value) {
        return String(value || '').trim().toLowerCase();
    }

    /**
     * Reads active UTM campaign data from the shared campaign session or legacy inputs.
     * @returns {{utmCampaigns: string[], hasUtmCampaign: Function}}
     */
    static getCampaignSession() {
        if (window.readersCampaignSession) {
            return window.readersCampaignSession;
        }

        let utmCampaigns = [];

        try {
            utmCampaigns = JSON.parse(sessionStorage.getItem('readersCampaignUtmCampaigns') || '[]');
        } catch (_) {
            utmCampaigns = [];
        }

        const queryUtmCampaign = UrlUtils.getQueryParam('utm_campaign');
        const cookieUtmCampaign = CookieUtils.getCookie('utm') || CookieUtils.getCookie('utm_campaign_cached');

        if (queryUtmCampaign) utmCampaigns.push(queryUtmCampaign);
        if (cookieUtmCampaign) utmCampaigns.push(cookieUtmCampaign);

        utmCampaigns = Array.from(new Set(utmCampaigns.map(this.normalizeCampaignToken).filter(Boolean)));

        return {
            utmCampaigns,
            hasUtmCampaign: (campaign) => {
                const normalizedCampaign = this.normalizeCampaignToken(campaign);
                return normalizedCampaign ? utmCampaigns.includes(normalizedCampaign) : utmCampaigns.length > 0;
            },
        };
    }

    /**
     * Checks if an element's campaign targeting matches the active campaign session.
     * @param {HTMLElement} item Element containing campaign data attributes.
     * @returns {{required: boolean, matches: boolean}}
     */
    static getCampaignMatch(item) {
        const legacyUtmRequired = item.dataset.utmRequired == 'true';
        const campaignUtm = item.dataset.campaignUtm || '';
        const required = legacyUtmRequired || campaignUtm !== '';

        if (!required) {
            return { required: false, matches: false };
        }

        const session = this.getCampaignSession();
        const targetUtm = campaignUtm || item.dataset.utmId || window.utm?.id || '';
        const matches = targetUtm ? session.hasUtmCampaign(targetUtm) : session.utmCampaigns.length > 0;

        return { required: true, matches };
    }

    /**
     * Checks if any campaign-targeted element matches the active campaign session.
     *
     * @param {NodeList|HTMLCollection} elements A collection of elements.
     * @returns {boolean}
     */
    static checkIfMatchingCampaignElement(elements) {
        return Array.from(elements)
            .some(item => {
                const campaignMatch = this.getCampaignMatch(item);
                return campaignMatch.required && campaignMatch.matches && DateUtils.checkDateBetween(item.dataset.timezone, item.dataset.dateFrom, item.dataset.dateTo);
            });
    }

    /**
     * Checks if any of the given elements require the user to be logged in.
     *
     * @param {NodeList|HTMLCollection} elements - The collection of elements to check.
     * @returns {boolean} - Returns true if at least one element has a data attribute 'loggedRequired' set to 'true' and the item is not schedule or expired, otherwise false.
     */
    static checkIfRequiredLogged(elements) {
        //todo maybe : at least exist one content utm but with no future or past date scheduling
        return Array.from(elements)
            .some(item => item.dataset.loggedRequired == 'true' && DateUtils.checkDateBetween(item.dataset.timezone, item.dataset.dateFrom, item.dataset.dateTo));
    }

    /**
     * Determines whether an item should be removed based on various conditions.
     *
     * @param {boolean} isUserLogged - Indicates if the user is logged in.
     * @param {boolean} loggedInRequired - Indicates if being logged in is required.
     * @param {boolean} isOneLoggedRequiredPresent - Indicates if at least one logged-in requirement is present.
     * @param {{required: boolean, matches: boolean}} campaignMatch - Current element campaign targeting result.
     * @param {boolean} isOneMatchingCampaignPresent - Indicates if one campaign-targeted item matches the active session.
     * @param {boolean} isDateBetweenScheduleContent - Indicates if the current date is within the scheduled content date range.
     * @returns {boolean} - Returns true if the item should be removed, otherwise false.
     */
    static checkIfRemoveItem(isUserLogged, loggedInRequired, isOneLoggedRequiredPresent, campaignMatch, isOneMatchingCampaignPresent, isDateBetweenScheduleContent) {
        //hide element if date is not between schedule content
        if (!isDateBetweenScheduleContent) return true;

        //default case - hide element if user is not logged and logged required is true
        const defaultLoggedCase = !isUserLogged && loggedInRequired;
        //default case - hide element if campaign targeting is required but the active session does not match
        const defaultCampaignCase = campaignMatch.required && !campaignMatch.matches;

        const defaultCase = defaultLoggedCase || defaultCampaignCase;

        //first check user status - campaign user comes first
        const isCampaign = isOneMatchingCampaignPresent;
        const isLogged = isUserLogged && isOneLoggedRequiredPresent;

        //if user matches a campaign and current item is not campaign-targeted - remove
        if (isCampaign) {
            return !campaignMatch.required || !campaignMatch.matches;
        }

        //if user is logged and logged required is not present - remove
        if (isLogged) {
            return !loggedInRequired;
        }

        return defaultCase;
    }
}

class ContentScheduling extends SwiperSlider {
    constructor() {
        super();
        this.scheduleDateFrom = this.dataset.dateFrom;
        this.scheduleDateTo = this.dataset.dateTo;
        this.scheduleTimezone = this.dataset.timezone;
        this.loggedInRequired = this.dataset.loggedRequired == 'true';
        this._campaignSessionHandler = () => this.visibility();
        document.addEventListener('readers:campaign-session-change', this._campaignSessionHandler);
        this.visibility();
    }

    visibility() {
        const isDateBetweenScheduleContent = DateUtils.checkDateBetween(this.scheduleTimezone, this.scheduleDateFrom, this.scheduleDateTo);
        const isUserLogged = this.dataset.customer != '';
        const campaignMatch = SchedulingUtils.getCampaignMatch(this);
        const isOneMatchingCampaignPresent = SchedulingUtils.checkIfMatchingCampaignElement(document.querySelectorAll(this.tagName.toLowerCase()));
        const isOneLoggedRequiredPresent = SchedulingUtils.checkIfRequiredLogged(document.querySelectorAll(this.tagName.toLowerCase()));
        const removeElement = SchedulingUtils.checkIfRemoveItem(isUserLogged, this.loggedInRequired, isOneLoggedRequiredPresent, campaignMatch, isOneMatchingCampaignPresent, isDateBetweenScheduleContent);

        if (removeElement) {
            document.removeEventListener('readers:campaign-session-change', this._campaignSessionHandler);
            this.remove();
            return;
        }
        // Only initialise Swiper when a .swiper child is present.
        // Without this guard, initSlider() would pass null to new Swiper()
        // for non-slider uses such as the single announcement bar.
        if (this.querySelector('.swiper')) {
            this.initSlider();
        }
        this.style.display = 'block';
    }

}

customElements.define('content-scheduling', ContentScheduling);

class AnnouncementSlideScheduling extends HTMLElement {
    constructor() {
        super();
        this.scheduleDateFrom = this.dataset.dateFrom;
        this.scheduleDateTo = this.dataset.dateTo;
        this.scheduleTimezone = this.dataset.timezone;
        this.loggedInRequired = this.dataset.loggedRequired == 'true';
        this.slideshow = this.closest('slideshow-component');
        this._campaignSessionHandler = () => this.handleElement();
        document.addEventListener('readers:campaign-session-change', this._campaignSessionHandler);
        this.handleElement();
    }

    handleElement() {
        const isDateBetweenScheduleContent = DateUtils.checkDateBetween(this.scheduleTimezone, this.scheduleDateFrom, this.scheduleDateTo);
        const isUserLogged = this.dataset.customer != '';
        const campaignMatch = SchedulingUtils.getCampaignMatch(this);
        const isOneMatchingCampaignPresent = SchedulingUtils.checkIfMatchingCampaignElement(this.slideshow.querySelectorAll(this.tagName.toLowerCase()));
        const isOneLoggedRequiredPresent = SchedulingUtils.checkIfRequiredLogged(document.querySelectorAll(this.tagName.toLowerCase()));
        const removeElement = SchedulingUtils.checkIfRemoveItem(isUserLogged, this.loggedInRequired, isOneLoggedRequiredPresent, campaignMatch, isOneMatchingCampaignPresent, isDateBetweenScheduleContent);

        if (removeElement) {
            document.removeEventListener('readers:campaign-session-change', this._campaignSessionHandler);
            this.remove();
            this.slideshow.setAttribute('data-reset', true);
            return;
        }
        this.style.display = 'block';
    }

}

customElements.define('announcement-slide-scheduling', AnnouncementSlideScheduling);